import { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/base/Navbar';
import Footer from '@/components/base/Footer';
import useScrollToTop from '@/hooks/useScrollToTop';
import { allComics, getChapters, getRelatedComics, ComicDetail } from '@/mocks/comics';

export default function ComicDetailPage() {
  useScrollToTop();
  const { id } = useParams<{ id: string }>();
  const comicId = Number(id);
  const comic = useMemo(() => allComics.find((c) => c.id === comicId) as ComicDetail | undefined, [comicId]);
  const chapters = useMemo(() => getChapters(comicId), [comicId]);
  const related = useMemo(() => getRelatedComics(comicId), [comicId]);

  const [viewMode, setViewMode] = useState<'asc' | 'desc'>('desc');
  const [searchChapter, setSearchChapter] = useState('');

  const filteredChapters = useMemo(() => {
    let result = [...chapters];
    if (viewMode === 'asc') result.reverse();
    if (searchChapter.trim()) {
      result = result.filter((ch) =>
        ch.title.includes(searchChapter.trim()) || String(ch.number).includes(searchChapter.trim())
      );
    }
    return result;
  }, [chapters, viewMode, searchChapter]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [comicId]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-20 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
          <p className="text-foreground-500">漫画未找到</p>
          <Link to="/" className="text-sm text-primary-500 hover:underline mt-4 inline-block">返回首页</Link>
        </main>
        <Footer />
      </>
    );
  }

  const pageUrl = `http://m99re.com/comic/${comic.id}`;
  const categorySlug = getCategorySlug(comic.category);
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comic.title,
    author: { '@type': 'Person', name: comic.author },
    image: comic.cover,
    description: comic.longDescription,
    url: pageUrl,
    datePublished: '2020-01-01',
    dateModified: comic.updateTime,
    genre: comic.category,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: 10,
      reviewCount: Math.floor(comic.chapters * 12),
    },
    isPartOf: {
      '@type': 'WebSite',
      name: '韩国漫画网',
      url: 'http://m99re.com/',
    },
    breadcrumb: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'http://m99re.com/' },
        { '@type': 'ListItem', position: 2, name: `${comic.category}漫画`, item: `http://m99re.com/category/${categorySlug}` },
        { '@type': 'ListItem', position: 3, name: comic.title, item: pageUrl },
      ],
    },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Navbar />
      <main>
        <section className="pt-24 pb-8 md:pt-32 md:pb-10 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              <Link to={`/category/${getCategorySlug(comic.category)}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">
                {comic.category}漫画
              </Link>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              <span className="text-foreground-800 font-medium whitespace-nowrap">{comic.title}</span>
            </nav>

            <div className="flex flex-col md:flex-row gap-8 md:gap-10">
              <div className="shrink-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 韩国漫画网`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-xs text-foreground-500">作者：<strong className="text-foreground-800">{comic.author}</strong></span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-accent-100 text-accent-900 whitespace-nowrap">
                    {comic.category}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-200 text-foreground-700 whitespace-nowrap">
                    {comic.status}
                  </span>
                </div>
                <div className="flex items-center gap-5 mb-5">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-sm text-accent-500 w-4 h-4 flex items-center justify-center"></i>
                    <span className="text-lg font-bold text-foreground-900">{comic.rating}</span>
                    <span className="text-xs text-foreground-400">/ 10</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-foreground-500">
                    <i className="ri-book-open-line w-4 h-4 flex items-center justify-center"></i>
                    <span>共 <strong className="text-foreground-800">{comic.chapters}</strong> 话</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-foreground-500">
                    <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                    <span>最后更新：{comic.updateTime}</span>
                  </div>
                </div>
                <div className="bg-background-50 rounded-lg p-4 md:p-5 border border-background-200/70">
                  <h2 className="text-xs font-label uppercase tracking-wider text-foreground-400 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.longDescription}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-10 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <h2 className="text-xl font-heading font-bold text-foreground-950">章节目录</h2>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="搜索章节..."
                    value={searchChapter}
                    onChange={(e) => setSearchChapter(e.target.value)}
                    className="pl-8 pr-3 py-1.5 rounded-full text-xs border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 w-36"
                  />
                  <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-foreground-400"></i>
                </div>
                <button
                  onClick={() => setViewMode(viewMode === 'desc' ? 'asc' : 'desc')}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={`w-3 h-3 flex items-center justify-center ${viewMode === 'desc' ? 'ri-sort-desc' : 'ri-sort-asc'}`}></i>
                  {viewMode === 'desc' ? '倒序' : '正序'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {filteredChapters.map((ch) => (
                <Link
                  key={ch.number}
                  to={`/comic/${comicId}`}
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 border border-background-200/60 hover:border-primary-200 hover:bg-background-100 transition-colors group"
                >
                  <span className="text-sm text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {ch.title}
                  </span>
                  <span className="text-xs text-foreground-400 shrink-0 ml-2">{ch.updateTime}</span>
                </Link>
              ))}
            </div>

            {filteredChapters.length === 0 && (
              <div className="text-center py-10">
                <p className="text-foreground-400 text-sm">未找到匹配的章节</p>
              </div>
            )}
          </div>
        </section>

        {related.length > 0 && (
          <section className="py-8 md:py-10 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                {related.map((r) => (
                  <Link key={r.id} to={`/comic/${r.id}`} className="group block">
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-2">
                      <img
                        src={r.cover}
                        alt={r.title}
                        title={`${r.title} - 韩国漫画网`}
                        loading="lazy"
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-background-50/90 text-foreground-700">
                        {r.status}
                      </div>
                    </div>
                    <h3 className="text-xs font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {r.title}
                    </h3>
                    <div className="flex items-center gap-1 mt-0.5">
                      <i className="ri-star-fill text-[9px] text-accent-500 w-3 h-3 flex items-center justify-center"></i>
                      <span className="text-[10px] text-foreground-500">{r.rating}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}

function getCategorySlug(category: string): string {
  const map: Record<string, string> = {
    '恋爱': 'romance', '热血': 'action', '悬疑': 'mystery', '都市': 'urban',
    '奇幻': 'fantasy', '校园': 'school', '美食': 'food', '科幻': 'scifi',
    '历史': 'history', '运动': 'sports',
  };
  return map[category] || '';
}